import type { AxiosInstance } from "axios";
import type { UpdateFeatureConfigInputType } from "@shared/schemas/feature-config-input.schemas";
import type { ConfigContext } from "@shared/schemas/config-context.schemas";
import {
  EventIdPublic,
  EventParticipantIdPublic,
  OrganizationIdPublic,
  UserIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type { EventFeaturePresetKey } from "@shared/schemas/event-feature-preset.schemas";

const FEATURE_CONFIG_API_PATH = "/api/v1/feature-configs";

function buildEventPresetPath(eventIdPublic: EventIdPublic): string {
  return `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/feature-configs/presets`;
}

export function createFeatureConfigApi(apiClient: AxiosInstance) {
  async function resolveConfigs(input: {
    readonly context: ConfigContext;
  }): Promise<readonly unknown[]> {
    const response = await apiClient.post<readonly unknown[]>(
      `${FEATURE_CONFIG_API_PATH}/resolved`,
      { context: input.context },
    );
    return response.data;
  }

  async function resolveConfigByKey(
    featureKey: string,
    input: { readonly context: ConfigContext },
  ): Promise<unknown> {
    const response = await apiClient.post<unknown>(
      `${FEATURE_CONFIG_API_PATH}/resolved/${encodeURIComponent(featureKey)}`,
      { context: input.context },
    );
    return response.data;
  }

  async function updateConfig(
    featureKey: string,
    input: {
      readonly scope: ConfigContext;
      readonly override: UpdateFeatureConfigInputType;
    },
  ): Promise<unknown> {
    const response = await apiClient.put<unknown>(
      `${FEATURE_CONFIG_API_PATH}/${encodeURIComponent(featureKey)}`,
      { scope: input.scope, override: input.override },
    );
    return response.data;
  }

  async function updateConfigs(input: {
    readonly scope: ConfigContext;
    readonly configs: ReadonlyArray<{
      readonly featureKey: string;
      readonly override: UpdateFeatureConfigInputType;
    }>;
  }): Promise<readonly unknown[]> {
    const response = await apiClient.put<readonly unknown[]>(
      `${FEATURE_CONFIG_API_PATH}`,
      {
        scope: input.scope,
        configs: input.configs,
      },
    );
    return response.data;
  }

  async function resetConfig(
    featureKey: string,
    scope: ConfigContext,
  ): Promise<unknown> {
    const params: Record<string, string> = {};
    const eventIdPublic = scope["eventIdPublic"];
    if (eventIdPublic !== null && eventIdPublic !== undefined) {
      params["eventIdPublic"] = EventIdPublic.unwrap(eventIdPublic);
    }
    const organizationIdPublic = scope["organizationIdPublic"];
    if (organizationIdPublic !== null && organizationIdPublic !== undefined) {
      params["organizationIdPublic"] =
        OrganizationIdPublic.unwrap(organizationIdPublic);
    }
    const userIdPublic = scope["userIdPublic"];
    if (userIdPublic !== null && userIdPublic !== undefined) {
      params["userIdPublic"] = UserIdPublic.unwrap(userIdPublic);
    }
    const participantIdPublic = scope["participantIdPublic"];
    if (participantIdPublic !== null && participantIdPublic !== undefined) {
      params["participantIdPublic"] =
        EventParticipantIdPublic.unwrap(participantIdPublic);
    }
    const channel = scope["channel"];
    if (channel !== null && channel !== undefined) {
      params["channel"] = JSON.stringify(channel);
    }
    const response = await apiClient.delete<unknown>(
      `${FEATURE_CONFIG_API_PATH}/${encodeURIComponent(featureKey)}`,
      { params },
    );
    return response.data;
  }

  async function applyEventPreset(
    eventIdPublic: EventIdPublic,
    presetKey: EventFeaturePresetKey,
  ): Promise<unknown> {
    const response = await apiClient.post<unknown>(
      `${buildEventPresetPath(eventIdPublic)}/${encodeURIComponent(presetKey)}/apply`,
    );
    return response.data;
  }

  return {
    resolveConfigs,
    resolveConfigByKey,
    updateConfig,
    updateConfigs,
    resetConfig,
    applyEventPreset,
  } as const;
}
